import React, { useEffect, useMemo } from 'react';

import { Accordion } from '../../../components/core';
import TitleAndAbstract from './TitleAndAbstract';
import FullTextReview from './FullTextReview';
import Extraction from './Extraction';
import ImportStudiesNotification from './ImportStudiesNotification';
import ImportReferencesSummary from './ImportReferencesSummary';
import ReviewLinkContext from './ReviewLinkContext';
import ReviewSummaryContext from './ReviewSummaryContext';
import { ReviewMetricsQuestion } from './ReviewMetricsQuestion/ReviewMetricsQuestion';
import ReviewSetupBasicWizard from './ReviewSetupBasicWizard';
import { showStatisticsDelayToast } from './statisticsToast';
import { useStageStatistics } from './stageStatistics';
import { firstOpenView, HomeView } from './utils';
import { ReviewFeaturesProvider } from 'contexts/ReviewFeaturesProvider';
import { useReviewSummary } from 'query/review/ReviewStatistics';
import Spinner from 'system/elements/Spinner';
import { Stack } from 'components/layout';

export type Stage = 'screen' | 'select' | 'included';

export type Permissions = {
  consensusRequired: boolean;
};

export type Statistics = {
  contribution: number;
  complete: number;
  partiallyComplete: number;
  available: number;
  notStarted: number;
  consensusRequired: number;
};

export type ReviewHomeProps = {
  importsLink: string;
  newImportLink: string;
  reviewId: string;
  reviewLink: string;
  permissions: Permissions;
  reviewerName: string;
  stage: 'screen' | 'select' | 'included';
  isAutoExclusionEnabled: boolean;
  isAutoExclusionSupported: boolean;
  isMetricsRequested: boolean;
};

const Home = ({
  reviewId,
  reviewLink,
  reviewerName,
  importsLink,
  newImportLink,
  isAutoExclusionEnabled,
  isAutoExclusionSupported,
  isMetricsRequested,
}: ReviewHomeProps) => {
  const summary = useReviewSummary();
  const screen = useStageStatistics('screen');
  const select = useStageStatistics('select');
  const included = useStageStatistics('included');

  const hasSummaryFailed = summary.isError && !summary.data;
  const categoryCounts = summary.data?.categoryCounts;
  const hasExtractionTemplates = summary.data?.hasExtractionTemplates ?? false;
  const includedStudyCount = categoryCounts?.included ?? 0;

  const reviewSummary = useMemo(
    () => ({ hasExtractionTemplates, includedStudyCount }),
    [hasExtractionTemplates, includedStudyCount]
  );

  useEffect(() => {
    if (hasSummaryFailed) {
      showStatisticsDelayToast();
    }
  }, [hasSummaryFailed]);

  if (summary.isLoading) {
    return <Spinner />;
  }

  const openView: HomeView = categoryCounts
    ? firstOpenView(categoryCounts)
    : 'title_and_abstract';

  return (
    <ReviewLinkContext.Provider value={reviewLink}>
      <ReviewSummaryContext.Provider value={reviewSummary}>
        <ReviewFeaturesProvider>
          <Accordion>
            <Stack spacing={5}>
              <ReviewSetupBasicWizard
                isOpen={openView === 'review_setup_basic_wizard'}
              />

              <ImportStudiesNotification reviewId={reviewId} />

              <ImportReferencesSummary
                historyLink={importsLink}
                newImportLink={newImportLink}
                isOpen={openView === 'import_summary'}
                reviewId={reviewId}
                isAutoExclusionEnabled={isAutoExclusionEnabled}
                isAutoExclusionSupported={isAutoExclusionSupported}
              />

              <TitleAndAbstract
                isOpen={openView === 'title_and_abstract'}
                reviewerName={reviewerName}
                irrelevant={categoryCounts?.irrelevant ?? 0}
                stats={screen.stats}
                status={screen.status}
              />

              <FullTextReview
                isOpen={openView === 'full_text_review'}
                reviewerName={reviewerName}
                excluded={categoryCounts?.excluded ?? 0}
                stats={select.stats}
                status={select.status}
              />

              <Extraction
                isOpen={openView === 'extraction'}
                reviewerName={reviewerName}
                stats={included.stats}
                status={included.status}
              />

              <ReviewMetricsQuestion showModal={isMetricsRequested} />
            </Stack>
          </Accordion>
        </ReviewFeaturesProvider>
      </ReviewSummaryContext.Provider>
    </ReviewLinkContext.Provider>
  );
};

export default Home;
